import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { Link, useNavigate, useParams } from "react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import ModuleLayout from "../../components/admin/ModuleLayout"
import Alert from "@/components/ui/alert/Alert"
import { ChipInput } from "@/components/ui/ChipInput"
import { Toggle } from "@/components/admin/FormComponents"
import { SectionHeader } from "@/components/cccd/SectionHeader"
import { cccdAdmin, type EventCategoryInput, type SeedbedOption, type CatalogOption, type CccdAddon } from "@/lib/api/cccdAdminClient"
import { ScoringParametersSection } from "@/components/cccd/ScoringParametersSection"
import { slugify } from "@/lib/slugify"
import { toastError } from "@/lib/toast"
import { getValidationErrorMessage } from "@/lib/errors"
import { LoadState } from "@/components/ui/LoadState"

type FormState = {
  slug: string
  name: string
  audience: string
  description: string
  basePriceCop: string
  basePriceUsd: string
  availableMotors: string
  motorDescription: string
  imageKey: string
  features: string[]
  specificBenefits: string[]
  isActive: boolean
  seedbedId: string
  maxTeamSize: string
}

// addonId -> selección local (cccd-category-addons-and-pricing): "enabled"
// habilita el complemento para esta categoría, "isDefault" lo preselecciona.
type AddonSelection = Record<string, { enabled: boolean; isDefault: boolean }>

const emptyForm: FormState = {
  slug: "",
  name: "",
  audience: "",
  description: "",
  basePriceCop: "",
  basePriceUsd: "",
  availableMotors: "",
  motorDescription: "",
  imageKey: "",
  features: [],
  specificBenefits: [],
  isActive: true,
  seedbedId: "",
  maxTeamSize: "",
}

function toInput(form: FormState): Omit<EventCategoryInput, "orderIndex"> {
  return {
    slug: form.slug,
    name: form.name,
    audience: form.audience,
    description: form.description,
    basePriceCop: Number(form.basePriceCop) || 0,
    basePriceUsd: Number(form.basePriceUsd) || 0,
    availableMotors: form.availableMotors,
    motorDescription: form.motorDescription,
    // Deprecados por el catálogo de complementos (cccd-category-addons-and-pricing)
    // — se dejan en false; la migración B los retira del schema una vez nada los lea.
    allowKitSelection: false,
    allowAltimeterSelection: false,
    defaultKit: false,
    defaultAltimeter: false,
    imageKey: form.imageKey || null,
    features: form.features,
    specificBenefits: form.specificBenefits,
    isActive: form.isActive,
    // admin-list-drag-reorder: el orden ya no se elige en este formulario — el
    // backend lo asigna al final al crear, y una edición no debe tocarlo.
    seedbedId: form.seedbedId || null,
    maxTeamSize: form.maxTeamSize.trim() === "" ? null : Number(form.maxTeamSize),
  }
}

const formatCOP = (n: number) => "$ " + (n || 0).toLocaleString("es-CO")
const formatUSD = (n: number) => "US$ " + (n || 0).toLocaleString("en-US")

const Section = SectionHeader

const inputCls =
  "w-full h-11 rounded-[10px] border border-gray-300 px-3.5 text-sm text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
const labelCls = "mb-1.5 block text-[13px] font-medium text-gray-600 dark:text-gray-400"

export default function CategoryFormPage() {
  const { eventId, regionalId, categoryId } = useParams<{ eventId?: string; regionalId?: string; categoryId: string }>()
  const isRegional = Boolean(regionalId)
  const parentId = (eventId ?? regionalId)!
  const isEdit = Boolean(categoryId)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["event-categories", isRegional ? "regional" : "event", parentId],
    queryFn: () => (isRegional ? cccdAdmin.getRegionalCategories(parentId) : cccdAdmin.getEventCategories(parentId)),
    enabled: Boolean(parentId),
  })

  const { data: seedbedOptions = [], isError: seedbedsFailed } = useQuery<SeedbedOption[]>({
    queryKey: ["seedbed-options"],
    queryFn: () => cccdAdmin.listSeedbedOptions(),
  })

  const { data: motorOptions = [] } = useQuery<CatalogOption[]>({
    queryKey: ["catalog-options", "motor"],
    queryFn: () => cccdAdmin.listCatalogOptions("motor"),
  })

  const { data: addonCatalog = [] } = useQuery<CccdAddon[]>({
    queryKey: ["addons"],
    queryFn: () => cccdAdmin.listAddons(),
  })

  // El catálogo de complementos por categoría solo existe para categorías de
  // evento nacional — los regionales todavía no exponen ese sub-recurso.
  const { data: categoryAddonLinks } = useQuery({
    queryKey: ["category-addons", eventId, categoryId],
    queryFn: () => cccdAdmin.getCategoryAddons(eventId!, categoryId!),
    enabled: !isRegional && isEdit && Boolean(eventId) && Boolean(categoryId),
  })

  const [form, setForm] = useState<FormState>(emptyForm)
  const [addonSelection, setAddonSelection] = useState<AddonSelection>({})
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [imageError, setImageError] = useState("")

  // Vista previa en vivo — misma tarjeta que verá el equipo al inscribirse.
  const [previewCurrency, setPreviewCurrency] = useState<"COP" | "USD">("COP")
  const [previewOverrides, setPreviewOverrides] = useState<Record<string, boolean>>({})

  // Sugerencia de semillero (cccd-seedbed-team-sync): solo al crear una
  // categoría nueva sin semillero elegido todavía, buscando la categoría más
  // reciente con el mismo slug en una edición anterior.
  const { data: seedbedSuggestion } = useQuery({
    queryKey: ["category-seedbed-suggestion", form.slug],
    queryFn: () => cccdAdmin.suggestCategorySeedbed(form.slug),
    enabled: !isEdit && !form.seedbedId && form.slug.trim().length > 0,
  })
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isEdit || isLoading) return
    const cat = categories.find((c) => c.id === categoryId)
    if (cat) {
      setForm({
        slug: cat.slug,
        name: cat.name,
        audience: cat.audience ?? "",
        description: cat.description ?? "",
        basePriceCop: String(cat.basePriceCop ?? ""),
        basePriceUsd: cat.basePriceUsd != null ? String(cat.basePriceUsd) : "",
        availableMotors: cat.availableMotors ?? "",
        motorDescription: cat.motorDescription ?? "",
        imageKey: cat.imageKey ?? "",
        features: cat.features ?? [],
        specificBenefits: cat.specificBenefits ?? [],
        isActive: cat.isActive ?? true,
        seedbedId: cat.seedbedId ?? "",
        maxTeamSize: cat.maxTeamSize != null ? String(cat.maxTeamSize) : "",
      })
      setImagePreviewUrl(cat.imageUrl ?? null)
    }
  }, [isEdit, isLoading, categories, categoryId])

  // Semilla la selección local de complementos desde los vínculos ya
  // guardados — solo una vez que llegan, para no pisar clics del admin.
  useEffect(() => {
    if (!categoryAddonLinks) return
    setAddonSelection((prev) => {
      const next = { ...prev }
      for (const link of categoryAddonLinks) {
        next[link.addonId] = { enabled: true, isDefault: link.isDefault }
      }
      return next
    })
  }, [categoryAddonLinks])

  function toggleAddonEnabled(addonId: string, enabled: boolean) {
    setAddonSelection((prev) => ({
      ...prev,
      [addonId]: { enabled, isDefault: enabled ? (prev[addonId]?.isDefault ?? false) : false },
    }))
  }

  function toggleAddonDefault(addonId: string, isDefault: boolean) {
    setAddonSelection((prev) => ({ ...prev, [addonId]: { enabled: true, isDefault } }))
  }

  // Persiste la selección de complementos contra la categoría ya creada,
  // diffeando contra los vínculos que el servidor ya tenía (edit) o creando
  // todos los habilitados desde cero (create) — ver design.md, cccd-addon-catalog.
  async function saveAddonLinks(targetEventId: string, targetCategoryId: string) {
    const original = new Map((categoryAddonLinks ?? []).map((l) => [l.addonId, l.isDefault]))
    const selected = Object.entries(addonSelection).filter(([, sel]) => sel.enabled)

    const ops: Promise<unknown>[] = []
    for (const [addonId, sel] of selected) {
      if (!original.has(addonId)) {
        ops.push(cccdAdmin.enableCategoryAddon(targetEventId, targetCategoryId, addonId, sel.isDefault))
      } else if (original.get(addonId) !== sel.isDefault) {
        ops.push(cccdAdmin.updateCategoryAddon(targetEventId, targetCategoryId, addonId, sel.isDefault))
      }
    }
    for (const [addonId] of original) {
      if (!addonSelection[addonId]?.enabled) {
        ops.push(cccdAdmin.disableCategoryAddon(targetEventId, targetCategoryId, addonId))
      }
    }
    await Promise.all(ops)
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["event-categories", isRegional ? "regional" : "event", parentId] })
    qc.invalidateQueries({ queryKey: ["category-addons", eventId, categoryId] })
  }

  const createMutation = useMutation({
    mutationFn: (input: Partial<EventCategoryInput>) =>
      isRegional ? cccdAdmin.createRegionalCategory(parentId, input) : cccdAdmin.createEventCategory(parentId, input),
    onSuccess: invalidate,
  })
  const updateMutation = useMutation({
    mutationFn: (input: Partial<EventCategoryInput>) =>
      isRegional
        ? cccdAdmin.updateRegionalCategory(parentId, categoryId!, input)
        : cccdAdmin.updateEventCategory(parentId, categoryId!, input),
    onSuccess: invalidate,
  })

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function goBack() {
    navigate(isRegional ? `/cccd/regionals/${parentId}` : `/cccd/events/${parentId}?tab=configuracion`)
  }

  const availableMotorsList = form.availableMotors
    .split(",")
    .map((m) => m.trim())
    .filter(Boolean)

  function setAvailableMotorsList(list: string[]) {
    set("availableMotors", list.join(", "))
  }

  function addMotorFromCatalog(value: string) {
    const option = motorOptions.find((m) => m.value === value)
    if (!option || availableMotorsList.includes(option.label)) return
    setAvailableMotorsList([...availableMotorsList, option.label])
  }

  async function handleImageChange(file: File) {
    setUploadingImage(true)
    setImageError("")
    try {
      const { upload_url, key } = await cccdAdmin.getCategoryImageUploadUrl({
        filename: file.name,
        content_type: file.type || "image/png",
      })
      const putRes = await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "image/png" },
        body: file,
      })
      if (!putRes.ok) throw new Error(`Error subiendo la imagen: ${putRes.status}`)
      set("imageKey", key)
      setImagePreviewUrl(URL.createObjectURL(file))
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Error al subir la imagen"
      setImageError(message)
      toastError(message)
    } finally {
      setUploadingImage(false)
    }
  }

  function removeImage() {
    set("imageKey", "")
    setImagePreviewUrl(null)
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    setError("")
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(toInput(form))
        if (!isRegional) await saveAddonLinks(eventId!, categoryId!)
      } else {
        const created = await createMutation.mutateAsync(toInput(form))
        if (!isRegional) {
          try {
            await saveAddonLinks(eventId!, created.id)
          } catch (addonErr) {
            // La categoría ya existe: seguir en el formulario de "crear" la duplicaría (409 por slug).
            // Se pasa a editar la creada para reintentar solo los complementos.
            toastError(
              `La categoría "${form.name}" se creó, pero no se pudieron guardar sus complementos: ${getValidationErrorMessage(addonErr)} Selecciónalos de nuevo y guarda.`,
            )
            setSaving(false)
            navigate(`/cccd/events/${eventId}/categories/${created.id}/edit`)
            return
          }
        }
      }
      goBack()
    } catch (err: unknown) {
      const message = getValidationErrorMessage(err)
      setError(message)
      toastError(message)
      setSaving(false)
    }
  }

  // ─── Vista previa en vivo ────────────────────────────────────────────────
  const enabledAddons = addonCatalog
    .filter((a) => addonSelection[a.id]?.enabled)
    .map((a) => ({ ...a, isDefault: addonSelection[a.id]?.isDefault ?? false }))

  const isPreviewChecked = (addonId: string, isDefault: boolean) => previewOverrides[addonId] ?? isDefault
  const togglePreviewAddon = (addonId: string, isDefault: boolean) =>
    setPreviewOverrides((prev) => ({ ...prev, [addonId]: !isPreviewChecked(addonId, isDefault) }))

  const activeBase = previewCurrency === "COP" ? Number(form.basePriceCop) || 0 : Number(form.basePriceUsd) || 0
  const addonsTotal = enabledAddons
    .filter((a) => isPreviewChecked(a.id, a.isDefault))
    .reduce((sum, a) => sum + (previewCurrency === "COP" ? a.priceCop : a.priceUsd), 0)
  const formatActive = previewCurrency === "COP" ? formatCOP : formatUSD

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        { label: "CCCD", to: "/cccd" },
        {
          label: isRegional ? "Regionales" : "Nacionales",
          to: isRegional ? `/cccd/regionals/${parentId}` : `/cccd/events/${parentId}`,
        },
        isEdit ? "Editar categoría" : "Nueva categoría",
      ]}
      title={isEdit ? form.name || "Editar categoría" : "Nueva categoría"}
      subtitle="Categoría de competencia del evento"
      statusLabel={isEdit ? (form.isActive ? "Activa" : "Inactiva") : undefined}
      tabs={[]}
      metrics={[]}
      actions={
        <>
          <Toggle checked={form.isActive} onChange={(checked) => set("isActive", checked)} label="Categoría activa" />
          <button type="button" onClick={goBack} className="min-h-11 rounded-[10px] border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800">
            Cancelar
          </button>
          <button type="submit" form="category-form" disabled={saving} className="min-h-11 rounded-[10px] bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
            {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear categoría"}
          </button>
        </>
      }
    >
      {isEdit && isLoading ? (
        <LoadState />
      ) : (
        <form id="category-form" onSubmit={onSubmit} className="flex items-start gap-6">
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            <Section
              title="Imagen"
              subtitle="Opcional — se muestra en la tarjeta de la categoría"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" /><circle cx="9" cy="10" r="1.6" stroke="currentColor" strokeWidth="1.6" /><path d="M4 17l5-5 4 4 3-3 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg>}
            >
              {imagePreviewUrl ? (
                <div className="flex items-center gap-4">
                  <img src={imagePreviewUrl} alt="Vista previa" className="h-[90px] w-[120px] rounded-xl border border-gray-200 object-cover dark:border-gray-800" />
                  <div>
                    <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">Imagen cargada correctamente.</p>
                    <button type="button" onClick={removeImage} className="text-[13px] font-medium text-error-600 hover:underline">
                      Quitar imagen
                    </button>
                  </div>
                </div>
              ) : (
                <label className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-gray-300 p-7 text-center dark:border-gray-700 ${uploadingImage ? "opacity-60" : ""}`}>
                  <input type="file" accept="image/*" disabled={uploadingImage} className="hidden" onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) void handleImageChange(f)
                    e.target.value = ""
                  }} />
                  <div className="flex size-[38px] items-center justify-center rounded-[10px] bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 16V4M12 4l-4 4M12 4l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {uploadingImage ? "Subiendo…" : <><strong className="text-brand-500">Haz clic para subir</strong> o arrastra una imagen</>}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">PNG o JPG, recomendado 800×600px</p>
                </label>
              )}
              {imageError && (
                <p role="alert" aria-live="assertive" className="mt-2 text-xs text-error-500">
                  {imageError}
                </p>
              )}
            </Section>

            <Section
              title="Identificación"
              subtitle="Cómo se llama y a quién va dirigida esta categoría"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M4 12h10M4 17h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>}
            >
              <div className="space-y-4">
                <div>
                  <label htmlFor="category-form-name" className={labelCls}>Nombre <span className="text-error-500">*</span></label>
                  <input
                    id="category-form-name"
                    required
                    value={form.name}
                    onChange={(e) => {
                      const name = e.target.value
                      setForm((prev) => ({ ...prev, name, slug: isEdit ? prev.slug : slugify(name) }))
                    }}
                    placeholder="Ej. Categoría A — Principiantes"
                    className={inputCls}
                  />
                  <div className="mt-[7px] flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0"><rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" /></svg>
                    <span>Slug:</span>
                    <code className="rounded-md bg-gray-100 px-1.5 py-0.5 text-gray-600 dark:bg-gray-800 dark:text-gray-400">{form.slug || "—"}</code>
                    <span>{isEdit ? "no cambia al editar" : "se genera del nombre"}</span>
                  </div>
                </div>

                <div>
                  <label htmlFor="category-form-audience" className={labelCls}>Público objetivo</label>
                  <input id="category-form-audience" value={form.audience} onChange={(e) => set("audience", e.target.value)} placeholder="Ej. Estudiantes universitarios" className={inputCls} />
                </div>

                <div>
                  <label htmlFor="category-form-description" className={labelCls}>Descripción</label>
                  <textarea id="category-form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Descripción de la categoría" className={inputCls.replace("h-11", "min-h-[72px] py-2.5")} />
                </div>
              </div>
            </Section>

            <Section
              title="Precio base"
              subtitle="Dos tarifas, una por país de origen del equipo"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M15 9.5c0-1.4-1.3-2.5-3-2.5s-3 1-3 2.3c0 3 6 1.3 6 4.2 0 1.4-1.3 2.5-3 2.5s-3-1-3-2.3" /></svg>}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="category-form-price-cop" className="mb-1.5 flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                    <span className="size-1.5 rounded-full bg-warning-500" /> Colombia
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                    <input id="category-form-price-cop" required type="number" min="0" step="1" inputMode="numeric" value={form.basePriceCop} onChange={(e) => set("basePriceCop", e.target.value)} placeholder="0" className={`${inputCls} h-[42px] pl-7 pr-12`} />
                    <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">COP</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{formatCOP(Number(form.basePriceCop) || 0)}</p>
                </div>
                <div>
                  <label htmlFor="category-form-price-usd" className="mb-1.5 flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                    <span className="size-1.5 rounded-full bg-brand-500" /> Internacional
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                    <input id="category-form-price-usd" required type="number" min="0" step="1" inputMode="numeric" value={form.basePriceUsd} onChange={(e) => set("basePriceUsd", e.target.value)} placeholder="0" className={`${inputCls} h-[42px] pl-7 pr-12`} />
                    <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">USD</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{formatUSD(Number(form.basePriceUsd) || 0)}</p>
                </div>
              </div>
              <p className="mt-3.5 text-xs text-gray-500 dark:text-gray-400">
                El orden de esta categoría se define arrastrándola en la lista de categorías del evento, no aquí.
              </p>
            </Section>

            <Section
              title="Semillero"
              subtitle="Acceso a cursos al activarse un equipo de esta categoría"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l8 4-8 4-8-4 8-4z" strokeLinejoin="round" /><path d="M4 11v4c0 1.5 3.5 3 8 3s8-1.5 8-3v-4" /></svg>}
            >
              <div>
                <label className={labelCls}>Semillero (acceso a cursos)</label>
                <select
                  value={form.seedbedId}
                  onChange={(e) => set("seedbedId", e.target.value)}
                  disabled={seedbedsFailed}
                  className={inputCls}
                >
                  <option value="">Sin semillero asociado</option>
                  {form.seedbedId && seedbedsFailed && (
                    <option value={form.seedbedId}>Semillero actual (no se pudo cargar el nombre)</option>
                  )}
                  {seedbedOptions.map((sb) => (
                    <option key={sb.id} value={sb.id}>{sb.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Al activarse un equipo de esta categoría, sus miembros se inscriben automáticamente en este semillero.
                </p>
                {seedbedSuggestion && (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-500/10 dark:text-brand-300">
                    <span>
                      Sugerencia: usar <strong>{seedbedSuggestion.seedbedName ?? "este semillero"}</strong>, usado en «{seedbedSuggestion.sourceEventName}»
                    </span>
                    <button
                      type="button"
                      onClick={() => set("seedbedId", seedbedSuggestion.seedbedId)}
                      className="min-h-11 shrink-0 rounded-md border border-brand-300 px-2 py-1 font-medium hover:bg-brand-100 dark:border-brand-700 dark:hover:bg-brand-500/20"
                    >
                      Usar
                    </button>
                  </div>
                )}
                {seedbedsFailed && (
                  <Alert
                    variant="error"
                    title="No se pudo cargar la lista de semilleros"
                    message="No tienes permiso para verla o hubo un error de red. El semillero actual se mantiene sin cambios hasta que puedas recargarla."
                  />
                )}
              </div>

              <div className="mt-4" data-tour="category-max-team-size">
                <label className={labelCls}>Cupo máximo por equipo</label>
                <input
                  type="number"
                  min="1"
                  value={form.maxTeamSize}
                  onChange={(e) => set("maxTeamSize", e.target.value)}
                  placeholder="Sin límite"
                  className={`${inputCls} h-10 max-w-[160px]`}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  El equipo de esta categoría es el mismo grupo del semillero — este cupo limita cuántos
                  miembros puede tener en total. Vacío = sin límite.
                </p>
              </div>
            </Section>

            <Section
              title="Motores"
              subtitle="Propulsión oficial disponible para esta categoría"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 2c2.5 2.6 4 6 4 9.5 0 2-.6 3.7-1.4 5.2L12 14l-2.6 2.7C8.6 15.2 8 13.5 8 11.5 8 8 9.5 4.6 12 2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><circle cx="12" cy="9.5" r="1.3" stroke="currentColor" strokeWidth="1.4" /></svg>}
            >
              <div>
                <label className={labelCls}>Motores disponibles</label>
                <ChipInput
                  value={availableMotorsList}
                  onChange={setAvailableMotorsList}
                  placeholder="Escribe un motor y presiona Enter..."
                />
                {motorOptions.length > 0 ? (
                  <select
                    value=""
                    onChange={(e) => addMotorFromCatalog(e.target.value)}
                    className={`${inputCls} mt-2 h-10`}
                  >
                    <option value="">Agregar desde el catálogo de motores...</option>
                    {motorOptions
                      .filter((m) => !availableMotorsList.includes(m.label))
                      .map((m) => (
                        <option key={m.id} value={m.value}>{m.label}</option>
                      ))}
                  </select>
                ) : (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    No hay motores en el catálogo CCCD todavía. Agrégalos en Catálogos CCCD.
                  </p>
                )}
              </div>

              <div className="mt-3.5">
                <label className={labelCls}>Descripción de motores</label>
                <input value={form.motorDescription} onChange={(e) => set("motorDescription", e.target.value)} placeholder="Detalles sobre motores" className={`${inputCls} h-10`} />
              </div>
            </Section>

            {isEdit && (
              <DndProvider backend={HTML5Backend}>
                <ScoringParametersSection categoryId={categoryId!} />
              </DndProvider>
            )}

            {!isRegional && (
            <Section
              title="Complementos"
              subtitle="Addons disponibles para esta categoría"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>}
            >
              <div className="mb-0.5 flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Complementos (addons)</p>
                <Link to="/cccd/addons" className="flex items-center gap-1 text-xs font-medium text-brand-500 hover:text-brand-600">
                  Gestionar catálogo
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M7 5l6 5-6 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </Link>
              </div>
              <p className="mb-3.5 text-xs text-gray-500 dark:text-gray-400">
                Se definen una sola vez en el catálogo de complementos — aquí solo eliges cuáles aplican a esta categoría, no su precio.
              </p>

              {addonCatalog.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  No hay complementos en el catálogo todavía. <Link to="/cccd/addons" className="text-brand-500 hover:underline">Créalos aquí.</Link>
                </p>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {addonCatalog.map((addon) => {
                    const sel = addonSelection[addon.id]
                    const enabled = sel?.enabled ?? false
                    // Un complemento inactivo solo puede seguir mostrado si ya
                    // estaba habilitado para esta categoría — no se ofrece para
                    // activarlo de nuevo (cccd-addon-catalog).
                    const lockedInactive = !addon.isActive && !enabled
                    return (
                      <div key={addon.id} className="overflow-hidden rounded-xl border border-gray-100 dark:border-gray-800">
                        <label className="flex cursor-pointer items-center justify-between gap-3 px-3.5 py-3">
                          <span className="flex flex-col gap-px">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {addon.name}
                              {!addon.isActive && <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">(inactivo en el catálogo)</span>}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{formatCOP(addon.priceCop)} · {formatUSD(addon.priceUsd)}</span>
                          </span>
                          <Toggle checked={enabled} disabled={lockedInactive} onChange={(checked) => toggleAddonEnabled(addon.id, checked)} label="" ariaLabel={addon.name} />
                        </label>
                        {enabled && (
                          <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-3.5 py-2.5 dark:border-gray-800">
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              Preseleccionado <span className="text-gray-400">— el equipo puede quitarlo</span>
                            </span>
                            <Toggle
                              checked={sel?.isDefault ?? false}
                              onChange={(checked) => toggleAddonDefault(addon.id, checked)}
                              label=""
                              ariaLabel={`Preseleccionar ${addon.name}`}
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </Section>
            )}

            <Section
              title="Características y beneficios"
              subtitle="Lo que verá el equipo al comparar categorías"
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="m12 3 2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6-4.5-4.2 6.1-.7L12 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>}
            >
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>Características</label>
                  <ChipInput value={form.features} onChange={(val) => set("features", val)} placeholder="Escribe una característica y presiona Enter..." />
                </div>
                <div>
                  <label className={labelCls}>Beneficios específicos</label>
                  <ChipInput value={form.specificBenefits} onChange={(val) => set("specificBenefits", val)} placeholder="Escribe un beneficio y presiona Enter..." />
                </div>
              </div>
            </Section>

            {error ? <Alert variant="error" title="No se pudo guardar" message={error} /> : null}
          </div>

          {/* ── Vista previa en vivo ── */}
          <div className="sticky top-6 w-[380px] shrink-0 max-lg:hidden">
            <div className="mb-3 flex items-center gap-2 pl-0.5">
              <span className="size-[7px] rounded-full bg-success-500 shadow-[0_0_0_3px_rgba(18,183,106,0.18)]" />
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Vista previa en vivo — así la verá el equipo</p>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-gray-900">
              {imagePreviewUrl ? (
                <img src={imagePreviewUrl} alt="" className="h-[170px] w-full object-cover" />
              ) : (
                <div className="flex h-[170px] w-full items-center justify-center bg-gradient-to-br from-brand-500 to-brand-700">
                  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" className="text-white/85"><path d="M12 2c2.5 2.6 4 6 4 9.5 0 2-.6 3.7-1.4 5.2L12 14l-2.6 2.7C8.6 15.2 8 13.5 8 11.5 8 8 9.5 4.6 12 2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /><circle cx="12" cy="9.5" r="1.4" stroke="currentColor" strokeWidth="1.4" /></svg>
                </div>
              )}

              <div className="p-5">
                <div className="flex items-start justify-between gap-2.5">
                  <h4 className="text-base font-semibold leading-tight text-gray-800 dark:text-white/90">{form.name || "Nombre de la categoría"}</h4>
                  <span className={`shrink-0 rounded-full px-2 py-[3px] text-[11px] font-medium ${form.isActive ? "bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400" : "bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400"}`}>
                    {form.isActive ? "Activa" : "Inactiva"}
                  </span>
                </div>
                <p className="mb-3.5 mt-1 text-[13px] text-gray-500 dark:text-gray-400">{form.audience || "Público objetivo"}</p>

                <div role="tablist" aria-label="Moneda de la vista previa" className="mb-3 inline-flex rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={previewCurrency === "COP"}
                    onClick={() => setPreviewCurrency("COP")}
                    className={`min-h-11 rounded-md px-3 py-1.5 text-xs font-semibold ${previewCurrency === "COP" ? "bg-white text-gray-800 shadow-sm dark:bg-gray-700 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}
                  >
                    Colombia · COP
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={previewCurrency === "USD"}
                    onClick={() => setPreviewCurrency("USD")}
                    className={`min-h-11 rounded-md px-3 py-1.5 text-xs font-semibold ${previewCurrency === "USD" ? "bg-white text-gray-800 shadow-sm dark:bg-gray-700 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}
                  >
                    Internacional · USD
                  </button>
                </div>

                <p className="mb-0.5 text-[13px] text-gray-500 dark:text-gray-400">Precio base</p>
                <p className="mb-3.5 text-xl font-bold text-gray-800 dark:text-white/90">{formatActive(activeBase)}</p>

                {availableMotorsList.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {availableMotorsList.map((m) => (
                      <span key={m} className="rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-600 dark:bg-brand-500/10 dark:text-brand-300">{m}</span>
                    ))}
                  </div>
                )}
                {form.motorDescription.trim() && (
                  <p className="mb-3.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{form.motorDescription}</p>
                )}

                {enabledAddons.length > 0 && (
                  <div className="mb-0.5 border-t border-gray-100 py-3 dark:border-gray-800">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Complementos — el equipo elige</p>
                    {enabledAddons.map((a) => {
                      const checked = isPreviewChecked(a.id, a.isDefault)
                      return (
                        <div key={a.id} className="flex items-center justify-between gap-2.5 py-1.5">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={checked}
                            onClick={() => togglePreviewAddon(a.id, a.isDefault)}
                            className="flex min-h-11 cursor-pointer items-center gap-2.5"
                          >
                            <span
                              aria-hidden="true"
                              className={`flex size-4 shrink-0 items-center justify-center rounded-[5px] border-[1.6px] ${checked ? "border-brand-500 bg-brand-500" : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"}`}
                            >
                              {checked && <svg width="9" height="7" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                            </span>
                            <span className="text-[13px] text-gray-700 dark:text-gray-300">{a.name}</span>
                          </button>
                          <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400">
                            + {previewCurrency === "COP" ? formatCOP(a.priceCop) : formatUSD(a.priceUsd)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}

                <div className="mb-3.5 flex items-center justify-between border-b border-gray-100 border-t border-t-gray-200 py-3 dark:border-gray-800 dark:border-t-gray-700">
                  <span className="text-[13px] font-semibold text-gray-800 dark:text-white/90">Total a pagar</span>
                  <span className="text-base font-bold text-brand-500">{formatActive(activeBase + addonsTotal)}</span>
                </div>

                <div className="flex flex-col gap-1.5">
                  {form.features.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-[13px] text-gray-600 dark:text-gray-400">
                      <span className="mt-1.5 size-1 shrink-0 rounded-full bg-gray-400" />
                      {f}
                    </div>
                  ))}
                  {form.specificBenefits.map((b) => (
                    <div key={b} className="flex items-start gap-2 text-[13px] font-medium text-success-700 dark:text-success-400">
                      <span className="mt-1.5 size-1 shrink-0 rounded-full bg-success-500" />
                      {b}
                    </div>
                  ))}
                </div>

                <button type="button" disabled className="mt-[18px] w-full cursor-default rounded-[10px] bg-brand-500 py-[11px] text-sm font-medium text-white">
                  Elegir esta categoría
                </button>
              </div>
            </div>

            <p className="mt-3 px-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              Esta tarjeta es exactamente lo que un equipo ve al inscribirse — edita a la izquierda y compárala aquí antes de guardar.
            </p>
          </div>
        </form>
      )}
    </ModuleLayout>
  )
}
